/**
 * Course state for "The 17:00 Release". Three separate ideas, kept apart:
 *  1. first-attempt score: each mission's criteria snapshot at its first commit
 *     (immutable for this attempt);
 *  2. current simulated project state: what the later missions build on;
 *  3. demonstrated mastery: whether each mission was completed, with or without
 *     a guided correction.
 * Persisted to localStorage after every commit so a reload can resume.
 */

export interface DdCrit { id: string; label: string; pts: number; ok: boolean; expect?: string; why?: string; yours?: string }
export interface DdMission {
  first?: { crits: DdCrit[]; at: number; summary: string };
  mastered?: boolean;
  corrected?: boolean;
  /** Learner saw the explanation and kept the result without retrying. */
  acknowledged?: boolean;
}
export interface DdHistory { mission: string; text: string; kind: "consequence" | "corrected" | "note" | "blocked" }

export interface DdState {
  v: 1;
  attempt: string;
  startedAt: number;
  video?: "watched" | "skipped";
  missions: Record<string, DdMission>;
  /** D1 workspace first choice. */
  workspace?: string;
  /** D2 agent scope: first committed profile and the currently applied one. */
  scopeFirst?: Record<string, string>;
  scope?: Record<string, string>;
  d2Action?: string;
  /** D5 test-lab carry-over (patch commit happens on S3, the test on S4). */
  d5?: { patch: string; checkpoints: string[]; fields: string[]; fixed?: boolean };
  /** D8 matrix carry-over to the CI screen. */
  d8?: { answers: Record<string, string>; ranAt?: string };
  dependencyBlocker?: boolean;
  candidate: "R7" | "R8";
  outcome?: "verified" | "hold";
  hold?: { reason: string; owner: string; next: string };
  history: DdHistory[];
  lastMission?: string;
  /** The learner's route: which missions are in it (full by default). */
  route?: { id: string; missions: string[] };
}

const KEY = "cf1700:state:v1";

function fresh(): DdState {
  return { v: 1, attempt: Math.random().toString(36).slice(2, 10), startedAt: Date.now(), missions: {}, candidate: "R7", history: [] };
}

let state: DdState = load();

function load(): DdState {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const s = JSON.parse(raw) as DdState;
      if (s && s.v === 1 && s.missions) return s;
    }
  } catch {
    /* private mode */
  }
  return fresh();
}

function save() {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

export const dd = {
  get(): DdState {
    return state;
  },
  reset() {
    state = fresh();
    save();
  },
  set(patch: Partial<DdState>) {
    state = { ...state, ...patch };
    save();
  },
  /** Store the first committed criteria for a mission. Later commits are ignored. */
  commitFirst(mission: string, crits: DdCrit[], summary: string): DdCrit[] {
    const m = state.missions[mission] ?? {};
    if (!m.first) {
      state = { ...state, missions: { ...state.missions, [mission]: { ...m, first: { crits, at: Date.now(), summary } } }, lastMission: mission };
      save();
    }
    return state.missions[mission].first!.crits;
  },
  first(mission: string) {
    return state.missions[mission]?.first;
  },
  master(mission: string, corrected: boolean) {
    const m = state.missions[mission] ?? {};
    if (m.mastered) return;
    state = { ...state, missions: { ...state.missions, [mission]: { ...m, mastered: true, corrected: corrected || !!m.corrected } }, lastMission: mission };
    save();
  },
  markCorrected(mission: string) {
    const m = state.missions[mission] ?? {};
    state = { ...state, missions: { ...state.missions, [mission]: { ...m, corrected: true } } };
    save();
  },
  /** Learner kept a less-than-perfect result after reading the explanation. */
  acknowledge(mission: string) {
    const m = state.missions[mission] ?? {};
    if (m.mastered || m.acknowledged) return;
    state = { ...state, missions: { ...state.missions, [mission]: { ...m, acknowledged: true } }, lastMission: mission };
    save();
  },
  log(mission: string, text: string, kind: DdHistory["kind"]) {
    if (state.history.some((h) => h.mission === mission && h.text === text)) return;
    state = { ...state, history: [...state.history, { mission, text, kind }] };
    save();
  },
  score(): number {
    return Object.values(state.missions).reduce((sum, m) => sum + (m.first?.crits ?? []).reduce((s, c) => s + (c.ok ? c.pts : 0), 0), 0);
  },
  missionScore(mission: string): number | null {
    const f = state.missions[mission]?.first;
    if (!f) return null;
    return f.crits.reduce((s, c) => s + (c.ok ? c.pts : 0), 0);
  },
  mastery(): { mastered: number; corrected: number } {
    const ms = Object.values(state.missions);
    return { mastered: ms.filter((m) => m.mastered).length, corrected: ms.filter((m) => m.mastered && m.corrected).length };
  },
  /** Missions in the learner's route, in course order (all ten by default). */
  route(): string[] {
    const r = state.route?.missions;
    return r && r.length ? MISSION_ORDER.filter((m) => r.includes(m)) : [...MISSION_ORDER];
  },
  setRoute(id: string, missions: string[]) {
    state = { ...state, route: { id, missions: MISSION_ORDER.filter((m) => missions.includes(m)) } };
    save();
    if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("dd-state"));
  },
  /** Add one mission to the route (a manual jump); the route becomes custom. */
  addToRoute(mission: string) {
    const r = this.route();
    if (r.includes(mission)) return;
    this.setRoute("custom", [...r, mission]);
  },
};

/** Build a criteria list from a content list and a pass map (2 points unless
 *  set). `yours` optionally records what the learner chose per criterion. */
export function crits(
  defs: { id: string; label: string; pts?: number; expect?: string; why?: string }[],
  pass: Record<string, boolean>,
  yours?: Record<string, string>,
): DdCrit[] {
  return defs.map((d) => ({ id: d.id, label: d.label, pts: d.pts ?? 2, ok: !!pass[d.id], expect: d.expect, why: d.why, yours: yours?.[d.id] }));
}

export const MISSION_ORDER = ["d1", "d2", "d3", "d4", "d5", "d6", "d7", "d8", "d9", "d10"];

const isDone = (m?: DdMission) => !!(m?.mastered || m?.acknowledged);

/** The mission a learner should resume at, or null for a fresh start. */
export function resumeTarget(): string | null {
  const done = MISSION_ORDER.filter((m) => isDone(state.missions[m]));
  if (!done.length) return null;
  const next = dd.route().find((m) => !isDone(state.missions[m]));
  return next ?? "replay";
}

/** Status of one mission for the course map and the replay. */
export function missionStatus(m: string): "done" | "corrected" | "kept" | "started" | "todo" {
  const s = state.missions[m];
  if (s?.mastered) return s.corrected ? "corrected" : "done";
  if (s?.acknowledged) return "kept";
  if (s?.first) return "started";
  return "todo";
}
